/**
 * Spellbook overlay (DESIGN 6.7): held open with Tab (Controls emits the
 * "spellbook" intent; main.ts calls show/hide). Three tabs:
 *  - Combos: the 44 worked examples of DESIGN 3 as cards with key chips,
 *    the cast mode and the effect, plus an "across modes" strip showing what
 *    the same queue does under Force / Area / Self / Weapon.
 *  - Magicks: the ten sequences of DESIGN 2.9.
 *  - Statuses: the DESIGN 2.7 matrix (what each status does, what applies
 *    and removes it, how statuses interact, damage multipliers).
 *  - Weaves: the discovery book (weavesTab.ts), rebuilt on every open.
 * Content is built on first open; the chosen tab is remembered.
 */

import type { CastMode } from '@shared/index.js';
import type { ClientState } from '../state/clientState.js';
import type { Socket } from '../net/socket.js';
import { type Screen, button, chip, clear, el, focusPanel, keyboardFocus, keycap, restoreFocus, storageGet, storageSet, trapTab } from './dom.js';
import { STORAGE_KEYS } from './helpers.js';
import { buildWeaves } from './weavesTab.js';
import { discoveries } from '../state/discoveries.js';
import {
  type ComboCard, type KeyToken, DAMAGE_COLUMNS, ENGINE, INCOMING_STATUSES, MULTIPLIER_ROWS, STATUS_MATRIX, STATUS_ORDER,
  assembleCombos, groupCombos, magickRows, statusRows,
} from './spellbookData.js';

export type SpellbookTab = 'combos' | 'magicks' | 'weaves' | 'statuses';
const TABS: readonly { id: SpellbookTab; label: string }[] = [
  { id: 'combos', label: 'Combos' },
  { id: 'magicks', label: 'Magicks' },
  { id: 'weaves', label: 'Weaves' },
  { id: 'statuses', label: 'Statuses' },
];

const MODE_SHORT: Readonly<Record<CastMode, string>> = { force: 'Force', area: 'Area', self: 'Self', weapon: 'Weapon' };
const STATUS_SHORT: Readonly<Record<string, string>> = {
  wet: 'Wet', burning: 'Burning', chilled: 'Chilled', frozen: 'Frozen', shocked: 'Shocked', knockdown: 'Knocked down', hasted: 'Hasted', slowed: 'Slowed',
};

function isTab(x: string | null): x is SpellbookTab {
  return x === 'combos' || x === 'magicks' || x === 'weaves' || x === 'statuses';
}

function tokenNode(tok: KeyToken): HTMLElement {
  switch (tok.kind) {
    case 'el': return chip(tok.el, tok.label);
    case 'then': return el('span', { class: 'then' }, 'then');
    case 'magick': return keycap('Space');
    case 'text': return el('span', { class: 'muted' }, tok.label);
  }
}

function comboCard(card: ComboCard): HTMLElement {
  const keys = el('div', { class: 'keys' }, card.note ? el('span', { class: 'note' }, card.note) : null, ...card.tokens.map(tokenNode));
  const across = card.across.length > 0
    ? el('div', { class: 'across' },
      ...card.across.map((m) => el('span', { class: `across-item${m.mode === card.mode ? ' across-on' : ''}` },
        el('span', { class: 'across-mode' }, MODE_SHORT[m.mode]), ' ', m.name)))
    : null;
  const queue = card.mode === 'queue' && card.queue.length > 0
    ? el('div', { class: 'queue-out' }, el('span', { class: 'muted' }, 'queue: '), ...card.queue.map((e) => chip(e, undefined, 'sm')))
    : null;
  return el('div', { class: `combo-card mode-${card.mode}` },
    el('div', { class: 'combo-head' }, el('span', { class: 'combo-n' }, `#${card.n}`), el('span', { class: 'combo-mode' }, card.modeLabel)),
    keys,
    queue,
    el('div', { class: 'combo-result' }, card.result),
    across,
  );
}

function buildCombos(): HTMLElement {
  const cards = assembleCombos(undefined, ENGINE);
  const wrap = el('div', { class: 'tab-body' },
    el('p', { class: 'muted intro' }, 'Every queue casts four ways. Each card names the worked example and, underneath, what the same keys do under the other three casts.'),
  );
  for (const group of groupCombos(cards)) {
    wrap.appendChild(el('h3', { class: 'group-title' }, group.label));
    wrap.appendChild(el('div', { class: 'combo-grid' }, ...group.cards.map(comboCard)));
  }
  return wrap;
}

function buildMagicks(): HTMLElement {
  return el('div', { class: 'tab-body' },
    el('p', { class: 'muted intro' }, 'Queue exactly this sequence, in order, then press ', keycap('Space'), '. Lightning magicks cannot be queued while wet.'),
    el('div', { class: 'magick-list' },
      ...magickRows().map((m) => el('div', { class: 'magick-row' },
        el('div', { class: 'magick-name' }, m.name),
        el('div', { class: 'keys' }, ...m.seq.map((e) => chip(e)), el('span', { class: 'then' }, '+'), keycap('Space')),
        el('div', { class: 'magick-desc' }, m.desc),
      )),
    ),
  );
}

function buildStatuses(): HTMLElement {
  const rows = statusRows();
  const table = el('table', { class: 'status-table' },
    el('thead', null, el('tr', null, el('th', null, 'Status'), el('th', null, 'Lasts'), el('th', null, 'Effect'), el('th', null, 'Applied by'), el('th', null, 'Removed by'))),
    el('tbody', null, ...rows.map((r) => el('tr', null,
      el('td', null, el('span', { class: `status-tag st-${r.id}` }, r.name)),
      el('td', { class: 'num' }, `${r.duration} s`),
      el('td', null, r.effect),
      el('td', null, r.appliedBy),
      el('td', null, r.removedBy),
    ))),
  );
  const matrix = el('table', { class: 'status-matrix' },
    el('thead', null, el('tr', null, el('th', null, 'has ↓ / gets →'), ...INCOMING_STATUSES.map((s) => el('th', null, STATUS_SHORT[s] ?? s)))),
    el('tbody', null, ...STATUS_ORDER.filter((s) => s !== 'hasted' && s !== 'slowed').map((row) => el('tr', null,
      el('th', null, el('span', { class: `status-tag st-${row}` }, STATUS_SHORT[row] ?? row)),
      ...INCOMING_STATUSES.map((col) => {
        const text = STATUS_MATRIX[row][col];
        return el('td', { class: text.includes('FROZEN') ? 'cell-hot' : text.startsWith('->') ? 'cell-replace' : '' }, text);
      }),
    ))),
  );
  const mult = el('table', { class: 'mult-table' },
    el('thead', null, el('tr', null, el('th', null, 'Target'), ...DAMAGE_COLUMNS.map((c) => el('th', null, c)))),
    el('tbody', null, ...MULTIPLIER_ROWS.map((m) => el('tr', null,
      el('th', null, m.condition),
      ...DAMAGE_COLUMNS.map((c) => el('td', { class: m.cells[c] ? 'cell-hot' : 'cell-dim' }, m.cells[c] ?? '·')),
    ))),
  );
  return el('div', { class: 'tab-body' },
    el('h3', { class: 'group-title' }, 'What each status does'),
    table,
    el('h3', { class: 'group-title' }, 'Status on status'),
    el('p', { class: 'muted intro' }, 'The target already has the row status and a spell applies the column one. Wet then Cold is the big one: frozen targets take triple damage.'),
    matrix,
    el('h3', { class: 'group-title' }, 'Damage multipliers'),
    el('p', { class: 'muted intro' }, 'They multiply: a wet, frozen target cannot exist (frozen replaces wet), but a frozen orc takes x1.5 x3 lightning.'),
    mult,
  );
}

export function mount(root: HTMLElement, state: ClientState, socket: Socket): Screen & { toggle(): void; openOn(id: SpellbookTab): void; readonly isOpen: boolean; readonly pinned: boolean } {
  void state;
  void socket;
  const tabBar = el('div', { class: 'tabs', role: 'tablist' });
  // Focusable so the keyboard can scroll it (Page Up / Page Down).
  const content = el('div', { class: 'book-content', tabindex: 0, role: 'region', 'aria-label': 'Spellbook pages' });
  const panel = el('div', { class: 'book-panel', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Spellbook', tabindex: -1 },
    el('div', { class: 'book-head' },
      el('h2', { class: 'book-title' }, 'Spellbook'),
      tabBar,
      el('span', { class: 'muted book-hint' }, 'hold ', keycap('Tab')),
      button('Close', () => hideBook(), 'btn btn-ghost btn-sm book-close'),
    ),
    content,
  );
  clear(root);
  root.classList.add('spellbook');
  root.appendChild(panel);
  root.hidden = true;

  const stored = storageGet(STORAGE_KEYS.spellbookTab);
  let tab: SpellbookTab = isTab(stored) ? stored : 'combos';
  const built = new Map<SpellbookTab, HTMLElement>();
  const tabButtons = new Map<SpellbookTab, HTMLButtonElement>();

  for (const t of TABS) {
    const b = button(t.label, () => select(t.id), 'tab');
    b.setAttribute('role', 'tab');
    tabButtons.set(t.id, b);
    tabBar.appendChild(b);
  }

  function bodyFor(id: SpellbookTab): HTMLElement {
    // The discovery book changes as you play, so it is built fresh each time.
    if (id === 'weaves') return buildWeaves(discoveries);
    let node = built.get(id);
    if (!node) {
      node = id === 'combos' ? buildCombos() : id === 'magicks' ? buildMagicks() : buildStatuses();
      built.set(id, node);
    }
    return node;
  }

  function select(id: SpellbookTab): void {
    tab = id;
    storageSet(STORAGE_KEYS.spellbookTab, id);
    for (const [key, b] of tabButtons) {
      b.classList.toggle('tab-on', key === id);
      b.setAttribute('aria-selected', String(key === id));
    }
    clear(content);
    content.appendChild(bodyFor(id));
    content.scrollTop = 0;
  }

  // Tapping the dimmed backdrop (outside the book) closes it: the only way out on a phone.
  root.addEventListener('pointerdown', (e) => { if (e.target === root) hideBook(); });

  // Escape closes it and Tab stays inside it. Capture phase so Escape never also clears the spell queue.
  const onKey = (ev: KeyboardEvent): void => {
    if (root.hidden) return;
    if (ev.code === 'Escape' && !ev.shiftKey) {
      ev.preventDefault();
      ev.stopPropagation();
      hideBook();
    } else if (pinned) {
      trapTab(ev, panel);
    }
  };
  window.addEventListener('keydown', onKey, true);

  let rendered = false;
  let opener: HTMLElement | null = null;
  /** Opened from its button rather than the held Tab key: it takes focus and keeps Tab inside until closed. */
  let pinned = false;
  /** `focus`: a player opened it (button), so move focus in; the held Tab key leaves focus alone. */
  function showBook(focus = false): void {
    if (!rendered) {
      rendered = true;
      select(tab);
    } else if (root.hidden && tab === 'weaves') {
      // reopened on the Weaves tab: it shows what was found since it was last open
      select(tab);
    }
    const wasHidden = root.hidden;
    if (wasHidden) {
      pinned = focus;
      opener = focus ? keyboardFocus() : null;
    }
    root.hidden = false;
    if (focus && wasHidden) focusPanel(panel);
  }
  function hideBook(): void {
    if (root.hidden) return;
    root.hidden = true;
    pinned = false;
    restoreFocus(opener, root);
    opener = null;
  }
  return {
    show: () => showBook(),
    hide: hideBook,
    toggle(): void { if (root.hidden) showBook(true); else hideBook(); },
    /** Opened on a given page, and pinned like the button's: it stays until closed. */
    openOn(id: SpellbookTab): void {
      rendered = true;
      select(id);
      showBook(true);
    },
    get isOpen(): boolean { return !root.hidden; },
    get pinned(): boolean { return pinned && !root.hidden; },
    dispose(): void {
      window.removeEventListener('keydown', onKey, true);
      clear(root);
      built.clear();
    },
  };
}
